import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useEditor, useNode } from '@craftjs/core'
import { getRandomId } from '@craftjs/utils'
import { Slot } from '@radix-ui/react-slot'
import { isEmpty, mapValues, omit, uniq } from 'lodash';
import { ArrowBigUp, CopyIcon, MoveIcon, TrashIcon } from 'lucide-react'
import { cn, DraggableAction, DraggableHandle, DraggableWrapper, DefaultTooltip } from 'twyne-new.ui'

import { useHTMLClassnames, usePureCraftProp } from '@/components/builder/utils'
import { ActionSave } from '@/modules/cms/views/post/components/builder/Content/LeftSidebar/Blocks/Actions'
import { useFormattingProps, useInteractionMode, useSelected } from '@/modules/cms/views/post/components/builder/context'

import { useHighlight } from '../utils/Highlight';
import { useCloseParent, useDefinitionContext } from '../utils/context';
import { useBlockDefinition } from '../utils/definition'
import { useCustomUtilities, useStylePatterns } from '../utils/tailwindConfig/hooks'

function useCloneNode(node) {
  const { query: { node: getNode, parseSerializedNode } } = useEditor()

  return useCallback(() => {
    const cloneNodesReducer = (newNodes, node) => {
      const serialized = getNode(node.id).toSerializedNode()
      const { nodes: childrenNodes, linkedNodes, parent, ...data } = serialized

      const newId = mapping.get(node.id)

      const newSerialized = {
        ...data,
        parent: node.id === rootNodeId ? null : mapping.get(parent),
        nodes: childrenNodes && childrenNodes.map(id => mapping.get(id)),
        linkedNodes: linkedNodes && mapValues(linkedNodes, id => mapping.get(id)),
      }

      newNodes[newId] = parseSerializedNode(newSerialized).toNode(newNode => {
        newNode.id = newId
      })

      return newNodes
    }

    const mapping = {
      get(id) {
        if (!this[id]) {
          this[id] = getRandomId()
        }
        return this[id]
      },
    }

    const { nodes, rootNodeId } = node.toNodeTree()

    return {
      nodes: Object.values(nodes).reduce(cloneNodesReducer, {}),
      rootNodeId: mapping.get(rootNodeId),
    }
  }, [node, getNode, parseSerializedNode])
}

export function useActions(id, closeParentId) {
  const { actions, query: { node: getNode } } = useEditor()

  const node = getNode(id)
  const { data: { custom: { canDuplicate, name }, parent: parentId, hidden } } = node.get()
  const index = parentId && getNode(parentId).descendants().indexOf(id)

  const isDraggable = node.isDraggable()
  const isDeletable = node.isDeletable()

  const handleSelfClone = useCloneNode(node)
  const { definition: { canSave } = {} } = useDefinitionContext()

  return {
    name,
    move: {
      enabled: isDraggable,
    },
    remove: {
      enabled: isDeletable,
      handler: () => actions.delete(id),
    },
    parent: {
      enabled: closeParentId && closeParentId !== 'ROOT',
      handler: () => actions.selectNode(closeParentId),
    },
    duplicate: {
      enabled: canDuplicate,
      handler: () => actions.addNodeTree(handleSelfClone(), parentId, index + 1),
    },
    visibility: {
      enabled: true,
      hidden,
      handler: () => actions.setHidden(id, !hidden),
    },
    save: {
      enabled: canSave,
    },
  }
}

function useUpdateDefinitionContext() {
  const { definition: { context: { useUpdate, useDelete } } } = useDefinitionContext()
  useUpdate()
  useDelete()
}

function SelectedBehaviors() {
  useCopyProperties()
  useSelectedNode()
  return null
}

function useCopyProperties() {
  const { query: { node: getNode }, actions: { selectNode, setProp, setCustom } } = useEditor()
  const { id, selected } = useNode(({ events: { selected } }) => ({ selected }));

  const { value: interactionMode, setValue: setInteractionMode } = useInteractionMode()
  const { value: formattingProps, setValue: setFormattingProps } = useFormattingProps()
  const { getDefinition } = useBlockDefinition()

  const node = getNode(id)
  const { data: { props: nodeProps, custom: { sidebarProps } } } = useMemo(() => node.get(), [node]);

  const handleCopyProperties = useCallback(() => {
    const definitions = getDefinition(id) || []
    const ownProps = definitions.props?.filter(prop => prop.own).map(prop => prop.name)

    const { properties } = nodeProps || {}
    if (!isEmpty(properties)) {
      setFormattingProps({
        stateProps: omit(properties, ownProps),
        sidebarProps,
      })
    }
    setInteractionMode('format_painter:past')
    selectNode()
  }, [id, nodeProps, sidebarProps, selectNode, setInteractionMode, setFormattingProps, getDefinition])

  const handlePastProperties = useCallback(() => {
    const { stateProps, sidebarProps } = formattingProps || {}
    if (!isEmpty(stateProps)) {
      setProp(id, props => {
        if (isEmpty(props.properties)) return;
        Object.assign(props.properties, stateProps);
      })
    }

    if (!isEmpty(sidebarProps)) {
      setCustom(id, custom => {
        if (!custom.sidebarProps?.length) return;
        custom.sidebarProps = uniq([...custom.sidebarProps, ...sidebarProps])
      })
    }

    setInteractionMode('')
    setFormattingProps({})
  }, [id, formattingProps, setProp, setCustom, setInteractionMode, setFormattingProps])

  useEffect(() => {
    if (selected) {
      if (interactionMode === 'format_painter:copy') {
        handleCopyProperties()
      }
      if (interactionMode === 'format_painter:past') {
        handlePastProperties(selected)
      }
    }
  }, [selected, interactionMode, handleCopyProperties, handlePastProperties])
}

function useSelectedNode() {
  const { id, selected } = useNode(({ events: { selected } }) => ({ selected }));
  const { setValue: setSelected } = useSelected()

  useEffect(() => {
    if (selected) {
      setSelected(selected => {
        if (selected.length <= 1) return [id]
      })
    }
  }, [selected, id, setSelected])
}

function replaceVhToCqh(className) {
  return className?.replace(/h-\[(\d+)vh\]/g, 'h-[$1cqh]')
}

function useParseClassName(properties) {
  const { value: customUtilities } = useCustomUtilities()
  const { value: stylePattern } = usePureCraftProp('properties.stylePattern')
  const { value: data } = useStylePatterns(stylePattern)
  const { definition } = useDefinitionContext()

  let props = {}

  if (definition.isShared) {
    props = { ...properties, ...data }
  } else if (stylePattern) {
    props = { ...data, ...properties }
  } else {
    props = properties
  }

  return useHTMLClassnames(props, customUtilities)
}

function useExternalPreviewPosition(anchorElement, active, containerElement) {
  const [position, setPosition] = useState(null)
  const lastPositionRef = useRef(null)

  const updatePosition = useCallback(() => {
    if (!anchorElement || !containerElement) return
    const rect = anchorElement.getBoundingClientRect()
    const { ownerDocument } = anchorElement
    const ownerWindow = ownerDocument?.defaultView
    const iframeElement = ownerWindow?.frameElement
    if (!ownerWindow || !iframeElement) return

    const iframeRect = iframeElement.getBoundingClientRect()
    const containerRect = containerElement.getBoundingClientRect()
    const iframeViewportWidth = ownerWindow?.innerWidth || ownerDocument?.documentElement?.clientWidth || 1
    const iframeViewportHeight = ownerWindow?.innerHeight || ownerDocument?.documentElement?.clientHeight || 1

    const scaleX = iframeRect.width / iframeViewportWidth
    const scaleY = iframeRect.height / iframeViewportHeight

    const nextPosition = {
      top: (iframeRect.top + (rect.top * scaleY)) - containerRect.top + containerElement.scrollTop,
      left: (iframeRect.left + (rect.left * scaleX)) - containerRect.left + containerElement.scrollLeft,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    }

    const previous = lastPositionRef.current
    if (previous && previous.top === nextPosition.top && previous.left === nextPosition.left && previous.width === nextPosition.width && previous.height === nextPosition.height) {
      return
    }

    lastPositionRef.current = nextPosition
    setPosition(nextPosition)
  }, [anchorElement, containerElement])

  const onResize = useCallback(() => {
    if (!anchorElement || !containerElement) return () => {}
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(anchorElement)
    containerElement.addEventListener('scroll', updatePosition, true)

    return () => {
      resizeObserver.disconnect()
      containerElement.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchorElement, containerElement, updatePosition])

  useEffect(() => {
    const element = anchorElement
    if (!element || !containerElement || !active) {
      lastPositionRef.current = null
      setPosition(null)
      return
    }

    updatePosition()
    return onResize()
  }, [anchorElement, active, containerElement, updatePosition, onResize])

  return position
}

const Tooltip = ({ content, children }) => {
  const [open, setOpen] = useState(false)
  const suppressedRef = useRef(false)

  const handleEnter = () => {
    if (suppressedRef.current) return
    setOpen(true)
  }

  const handleLeave = () => {
    suppressedRef.current = false
    setOpen(false)
  }

  const handleDown = () => {
    suppressedRef.current = true
    setOpen(false)
  }

  return <DefaultTooltip content={content} triggerProps={{ asChild: true }} open={open}>
    <span onPointerEnter={handleEnter} onPointerLeave={handleLeave} onPointerDown={handleDown} onBlur={handleLeave}>
      {children}
    </span>
  </DefaultTooltip>
}

export const Action = forwardRef(({ children, className, tag: Tag = DraggableAction, ...props }, ref) => <>
  <Tag ref={ref}
    className={cn(className, 'group-data-[highlighted=true]:bg-orange-400! group-data-[selected=true]:bg-blue-500 group-data-[selected=false]:bg-gray-500 cursor-pointer')}
    {...props}
  >
    {children}
  </Tag>
</>)

export const BasePreviewSlot = forwardRef(({ children, anchorClassName, attributes = {}, properties: { attributes: propertiesAttributes = {}, ...properties } = {}, ...props }, ref) => {
  const propertiesClassName = replaceVhToCqh(useParseClassName({ ...properties, ...props }))

  return <Slot
    {...attributes}
    {...propertiesAttributes}
    className={`${anchorClassName} ${propertiesClassName}`}
    ref={ref}
  >
    {children}
  </Slot>
})

function DragActionsBar({ drag, id, selected, hovered, highlighted, label, customActions }) {
  const closeParentId = useCloseParent()
  const { name, move, remove, parent, duplicate, save } = useActions(id, closeParentId)

  const moveAction = move.enabled ? <Tooltip content='Move'><Action ref={drag}>
    <MoveIcon size={12} />
  </Action></Tooltip> : null

  const deleteAction = remove.enabled ? <Tooltip content='Delete'><Action onClick={remove.handler}>
    <TrashIcon size={12} />
  </Action></Tooltip> : null

  const parentAction = parent.enabled ? <Tooltip content='Select Parent'><Action onClick={parent.handler}>
    <ArrowBigUp size={12} />
  </Action></Tooltip> : null

  const duplicateAction = duplicate.enabled ? <Tooltip content='Duplicate'><Action onClick={duplicate.handler}>
    <CopyIcon size={12} />
  </Action></Tooltip> : null

  const saveAction = save.enabled ? <Tooltip content='Save'><Action>
    <ActionSave nodeId={id} isActive={selected || hovered || highlighted} />
  </Action></Tooltip> : null

  const blockLabelAction = selected || highlighted ? <Action tag='div' className='text-xs text-white rounded-xs px-1.5 py-0.5' data-selected={selected || highlighted}>
    {label || name}
  </Action> : null

  return <div className='flex items-center gap-1 group absolute top-[-3px] left-[-3px]' data-selected={selected} data-highlighted={highlighted}>
    <div className='flex items-center'>
      {parentAction}
      {moveAction}
      {deleteAction}
      {duplicateAction}
      {saveAction}
    </div>
    {blockLabelAction}
    {customActions && <div>
      {customActions}
    </div>}
  </div>
}

const BasePreviewDragActions = ({ customActions, label }) => {
  const { connectors: { drag }, id, hovered, selected } = useNode(({ events: { hovered, selected } }) => ({ hovered, selected }))
  const { anchorElement } = useEditor(state => ({ anchorElement: state.nodes[id]?.dom || null }))
  const { highlighted, value: highlightedValue } = useHighlight({ id, selected })
  const portalRoot = document.getElementById('builder-content')

  const isActive = selected || hovered || highlighted
  const position = useExternalPreviewPosition(anchorElement, isActive, portalRoot)

  if (!portalRoot || !isActive || !position) {
    return null
  }

  const Actions = <div className='absolute pointer-events-none z-9' style={position} id='actions'>
    <DraggableWrapper hovered={hovered} selected={selected} highlighted={highlighted} className='absolute top-[-3px] right-[-3px] bottom-[-3px] left-[-3px] pointer-events-none'>
      <div className='pointer-events-none'>
        <DraggableHandle active={isActive} className={`z-50! w-max pointer-events-auto ${highlightedValue ? 'right-[-3px]' : ''}`}>
          <DragActionsBar drag={drag} id={id} selected={selected} hovered={hovered} highlighted={highlighted} label={label} customActions={customActions} />
        </DraggableHandle>
      </div>
    </DraggableWrapper>
  </div>

  return createPortal(Actions, portalRoot)
}

export function BasePreviewWrapper({ children, ...props }) {
  const { connectors: { connect }, selected } = useNode(({ events: { selected } }) => ({ selected }))

  useUpdateDefinitionContext()

  return <>
    {selected && <SelectedBehaviors />}
    <BasePreviewAnchor {...props} ref={connect}>
      {children}
    </BasePreviewAnchor>
  </>
}
const BasePreviewAnchor = forwardRef(({ children, customActions, label, ...props }, ref) => <>
  <BasePreviewDragActions customActions={customActions} label={label} />
  <Slot {...props} ref={ref} >
    {children}
  </Slot>
</>)

export default function BasePreview({ children, ...props }) {
  return <BasePreviewWrapper {...props}>
    <BasePreviewSlot>
      {children}
    </BasePreviewSlot>
  </BasePreviewWrapper>
}
